import {
  DndContext,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import React from 'react';
import type { ReactNode } from 'react';

interface BaseItem {
  id: UniqueIdentifier;
}

interface ISortableList<T extends BaseItem> {
  items: T[];
  displayedItems: T[];
  renderItem(item: T): ReactNode;
  onItemOrderUpdate: (items: T[]) => void;
  className?: string;
}

export const SortableList = <T extends BaseItem>({
  items,
  displayedItems,
  renderItem,
  onItemOrderUpdate,
  className,
}: ISortableList<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over?.id) {
          const activeIndex = items.findIndex(({ id }) => id === active.id);
          const overIndex = items.findIndex(({ id }) => id === over.id);
          onItemOrderUpdate(arrayMove(items, activeIndex, overIndex));
        }
      }}
    >
      <ul
        className={className}
        role="application"
      >
        <SortableContext
          items={items}
          strategy={verticalListSortingStrategy}
        >
          {displayedItems.map((item) => (
            <React.Fragment key={item.id}>{renderItem(item)}</React.Fragment>
          ))}
        </SortableContext>
      </ul>
    </DndContext>
  );
};
